<template>
  <section class="cohort">
    <div
      :class="[
        'ui-mobile-12',
        'ui-mobile-wrap',
        'ui-d-flex'
      ]"
    >
      <div class="cohort__header">
        <div class="cohort__controls">
          <div class="cohort__filter-wrapper">
            <div class="cohort__select-wrapper">
              <span class="cohort__label">{{ $t('filters.selectPeriod') }}</span>
              <PeriodWrapper
                class="cohort__period"
                :settings="dateRange"
                @update="setPeriod"
                @change="setRange"
              />
            </div>
            <UiFilters
              :use-filters="availableFilters"
              :is-short="true"
              :filters="filters"
              class="ui-m-md-l"
              popper-class="cohort__filters"
              :reset-label="$t('filters.reset')"
              :route="route"
              :width="450"
              @input="setFilters"
              @reset="reset"
            />
          </div>
          <div class="cohort__select-wrapper cohort__select-wrapper--multiple">
            <span class="cohort__label">{{ $t('cohort.type') }}</span>
            <UiSelect
              v-model="cohortType"
              :label="$t('cohort.type')"
              insert-label
              class="cohort__select"
              :list="cohortTypeOptions"
              :default-value="[]"
              :clearable="false"
              value-prop="id"
              label-prop="text"
            />
          </div>
          <div class="cohort__select-wrapper cohort__select-wrapper--multiple cohort__select-wrapper--short">
            <span class="cohort__label">{{ $t('cohort.group_by') }}</span>
            <UiSelect
              v-model="cohortGroupBy"
              :label="$t('cohort.group_by')"
              insert-label
              class="cohort__select"
              :list="cohortGroupByOptions"
              :clearable="false"
              :default-value="[]"
            />
          </div>
          <div class="cohort__select-wrapper cohort__select-wrapper--multiple">
            <span class="cohort__label">{{ $t('cohort.additional_metrics') }}</span>
            <UiSelect
              v-model="additionalMetrics"
              :inner-label="$t('cohort.additional_metrics')"
              :prepend-text="`${$t('cohort.additional_metrics')}: `"
              class="cohort__select"
              :placeholder="$t('cohort.additional_metrics')"
              :list="metricOptions"
              option-value-param="name"
              option-name-param="title"
              option-label-param="title"
              type="multiselect"
              multiple
            />
          </div>
        </div>

        <div class="cohort__actions">
          <ExportReport :disabled="$_.isEmpty(exportUrls)" :export-links="exportUrls" />
          <UiButton
            :disabled="loading"
            class="btn cohort__button-generate"
            @click="generateReport"
          >
            <i class="fas fa-refresh" />
            {{ $t('cohort.generate') }}
          </UiButton>
        </div>
      </div>
    </div>
    <div
      v-if="isDesktop"
      class="ui-table"
    >
      <CohortTable
        ref="table"
        :loading="loading"
        :table-data="tableData"
        :metrics-list="metricsList"
        :applied-additional-metrics="appliedAdditionalMetrics"
        :render-cohort-label="renderCohortLabel"
      />
    </div>
    <MobileCohort
      v-else
      class="cohort__mobile-table"
      :data="mobileCohortData"
    />
  </section>
</template>

<script>
import MobileCohort from '@/components/MobileCohort/Cohort.vue';
import Formaters from 'src/views/Dashboard/CustomReports/formaters';
import UrlMixin from 'src/components/Query/url-mixin';
import UrlMixinHooks from 'src/components/Query/url-mixin-hooks';
import formatCurrency from '@/mixins/format-currency';
import UiFilters from 'src/components/UiFilters/UiFilters.vue';
import InjectPlugins from 'src/components/Report/Components/inject-plugins';
import DateMixin from '@/views/Dashboard/Reports/dateMixin';
import PeriodWrapper from '@/views/Dashboard/Reports/Filters/PeriodWrapper.vue';
import ExportReport from '@/views/Dashboard/Reports/ExportReport.vue';
import CohortTable from './CohortTable.vue';

export default {
  mixins: [
    UrlMixin,
    UrlMixinHooks,
    formatCurrency,
    InjectPlugins,
    DateMixin,
  ],
  fetch,
  components: {
    ExportReport,
    PeriodWrapper,
    MobileCohort,
    UiFilters,
    CohortTable,
  },
  data() {
    return {
      route: 'cohort',
      loading: false,

      exportUrls: {},
      cohortType: 'registration',
      cohortGroupBy: 'daily',
      cohortGroupByApplied: 'daily',
      additionalMetrics: [],
      appliedAdditionalMetrics: [],
      dateRange: {
        from: '',
        to: '',
        period: '',
      },
      filters: this.getDefaultFilter(),

      tableData: {
        cohort_names: [],
        data: {},
      },
      formaters: Formaters,
      windowWidth: window.innerWidth,
    };
  },
  computed: {
    currencyLabel() {
      return this.getCurrencyLabel(this.$store.state.payments.currencyCode);
    },
    availableFilters() {
      const { referral_program_enabled, media_buying_enabled } = this.$store.state.profile.user;

      return [
        'players',
        'brand',
        ...(referral_program_enabled ? ['subAffiliate'] : []),
        'offersByAffiliateId',
        'trafficSourceByAffiliateId',
        'marketingMaterialId',
        ...(media_buying_enabled ? [ 'mediaBuyingRootCampaignId', 'mediaBuyingCampaignId'] : []),
        'playerCountryCode',
      ];
    },
    cohortTypeOptions() {
      return [
        {
          id: 'registration',
          text: this.$t('cohort.types.registration'),
        },
        {
          id: 'first_deposit',
          text: this.$t('cohort.types.first_deposit'),
        },
        {
          id: 'earliest_deposit',
          text: this.$t('cohort.types.earliest_deposit'),
        },
      ];
    },
    cohortGroupByOptions() {
      return [
        {
          id: 'daily',
          text: this.$t('customReports.day'),
        },
        {
          id: 'weekly',
          text: this.$t('customReports.week'),
        },
        {
          id: 'monthly',
          text: this.$t('customReports.month'),
        },
      ];
    },
    metricOptions() {
      return (this.$store.state.settings.reports?.cohort_report_metrics || [])
        .map((el) => this.formatMetric(el, this.currencyLabel));
    },
    metricsList() {
      return [
        {
          key: 'players',
          label: this.$t('filters.players'),
          renderValue: (data) => `${data.players_count} / ${data.players_percent_of_cohort}`,
          class: this.appliedAdditionalMetrics.length ? 'bold' : '',
        },
        ...this.appliedAdditionalMetrics.map((key) => {
          const metric = this.$store.state.settings.reports?.cohort_report_metrics?.find((el) => el.name === key);

          if (!metric) return null;

          const { name, title } = this.formatMetric(metric);

          return {
            key: name,
            label: title,
          };
        }).filter((el) => !!el),
      ];
    },
    cacheData() {
      return {
        cohortGroupBy: this.cohortGroupBy,
        cohortType: this.cohortType,
        additionalMetrics: this.additionalMetrics.length ? this.additionalMetrics : undefined,
        dateRange: this.dateRange,
        filters: this.filters,
      };
    },
    rows() {
      return this.$store.state.report.cpa_conversions;
    },
    mobileCohortData() {
      return this.tableData.cohort_names.map((cohortDate) => ({
        cohortName: this.renderCohortLabel(cohortDate),
        dates: Object.keys(this.tableData.data[cohortDate]).map((date) => ({
          id: `${cohortDate}-${date}`,
          date: this.renderCohortLabel(date),
          metrics: this.metricsList.map((metric) => {
            const { renderValue, ...restMetric } = metric;
            return {
              ...restMetric,
              value: renderValue ?
                renderValue(this.tableData.data[cohortDate][date]) :
                this.tableData.data[cohortDate][date][metric.key],
            };
          }),
          data: this.tableData.data[cohortDate][date],
        })),
      }));
    },
    isDesktop() {
      return this.windowWidth > 650;
    },
  },
  methods: {
    resetState() {
      this.tableData = {
        cohort_names: [],
        data: {},
      };
      this.exportUrls = {};
    },
    setFilters(val) {
      this.filters = val;
    },
    setPeriod({ from, to, period }) {
      this.dateRange = {
        from,
        to,
        period,
      };
    },
    setRange(val) {},
    generateReport() {
      const {
        traffic_type,
        players_filter,
        media_buying_campaign_id,
        ...restFilters
      } = this.$_.omit(this.filters, this.availableFilters);
      this.loading = true;
      this.tableData = {
        cohort_names: [],
        data: {},
      };
      this.resetData();

      let fixed = null;
      if (this.dateRange?.period) {
        fixed = this.fixRange(this.dateRange);
      }

      let period = fixed || this.dateRange;
      return this.$api.getCohortReport({
        cohort_granularity: this.cohortGroupBy,
        cohort_event_type: this.cohortType,
        metrics: this.additionalMetrics.length ? this.additionalMetrics : undefined,
        metrics_format: 'pretty',

        from: period.from,
        to: period.to,
        ...traffic_type && { traffic_type },
        ...players_filter && { players_filter },

        filters: Object.entries(restFilters).reduce((acc, [key, value]) => {
          if (value && value !== 'all') {
            acc[key] = {
              op: '=',
              value,
            };
          }

          return acc;
        }, {
          ...media_buying_campaign_id && ({
            media_buying_campaign_id: {
              op: '=',
              value: media_buying_campaign_id,
            },
          }),
        }),
      }).then((res) => {
        this.cacheQuery();

        this.tableData = res.payload;
        const { csv, xlsx } = res.misc;
        this.appliedAdditionalMetrics = this.$_.cloneDeep(this.additionalMetrics);
        this.cohortGroupByApplied = this.cohortGroupBy;
        this.exportUrls = { csv, xlsx };

        this.$nextTick(() => {
          this.$refs.table?.$refs?.table?.doLayout();
        });
      }).finally(() => this.loading = false);
    },
    async reset() {
      this.filters = this.getDefaultFilter();
    },
    getDefaultFilter() {
      return {
        traffic_type: '',
        players_filter: '',
        affiliate_offer_id: '',
        affiliate_id: '',
        traffic_source_id: '',
        marketing_material_id: '',
        media_buying_root_campaign_id: '',
        media_buying_campaign_id: '',
        player_country_code: '',

        site_id: 'all',
      };
    },
    resetData() {
      this.$refs?.table?.resetData();
    },
    renderCohortLabel(day) {
      switch (this.cohortGroupByApplied) {
        case 'daily':
          return this.$moment(day).locale(this.$i18n.locale).format('DD MMM YYYY');
        case 'weekly':
          return `<span>${this.getStartOfWeekLimited(day, this.dateRange.from)
            .locale(this.$i18n.locale)
            .format('DD MMM YYYY')}&nbsp;-</span> <span>${this.getEndOfWeekLimited(day, this.dateRange.to)
            .locale(this.$i18n.locale)
            .format('DD MMM YYYY')}</span>`;
        case 'monthly':
          const month = this.$moment(day).locale(this.$i18n.locale).format('MMMM YYYY');
          return month.charAt(0).toUpperCase() + month.slice(1);
        default:
          return this.$moment(day).locale(this.$i18n.locale).format('DD MMM YYYY');
      }
    },
    getStartOfWeekLimited(day, limitDay) {
      const startOfWeek = this.$moment(day).startOf('isoWeek');
      const minDate = this.$moment(limitDay);
      return startOfWeek.isBefore(minDate) ? minDate : startOfWeek;
    },
    getEndOfWeekLimited(day, limitDay) {
      const endOfWeek = this.$moment(day).endOf('isoWeek');
      const maxDate = this.$moment(limitDay);
      return endOfWeek.isAfter(maxDate) ? maxDate : endOfWeek;
    },
    handleResize() {
      this.windowWidth = window.innerWidth;
    },
  },
  beforeRouteLeave(to, from, next) {
    this.resetState();
    next();
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
  created() {
    if (!this.$_.isEmpty(this.urlQuery)) {
      this.filters = this.$_.get(this.urlQuery, 'filters', {});
      this.cohortType = this.$_.get(this.urlQuery, 'cohortType', 'registration');
      this.cohortGroupBy = this.$_.get(this.urlQuery, 'cohortGroupBy', 'daily');
      this.cohortGroupByApplied = this.$_.get(this.urlQuery, 'cohortGroupBy', 'daily');
      const metricKeys = this.metricOptions.map((el) => el.name);
      const availableMetrics = this.$_.intersection(metricKeys, this.$_.get(this.urlQuery, 'additionalMetrics', []));
      this.appliedAdditionalMetrics = availableMetrics;
      this.additionalMetrics = availableMetrics;

      if (
        this.$moment(this.$_.get(this.urlQuery, ['dateRange', 'from'])).isValid()
        && this.$moment(this.$_.get(this.urlQuery, ['dateRange', 'to'])).isValid()
      ) {
        const period = this.$_.get(this.urlQuery, ['dateRange', 'period']);

        let fixed = null;
        if (period) {
          fixed = this.fixRange(this.urlQuery.dateRange);
        }

        this.dateRange = fixed || this.urlQuery.dateRange;
      }
    }
  },
};
</script>

<style lang="scss">
@import 'src/assets/theme/default/CustomReports/cohort.scss';
</style>
